import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import './ep.css';
import { epApi, showError } from './api';
import { addDays, deadlineText, DECISION_LABEL, md, programName, STAGES, todayIso } from './format';
import { useEpUser, useLoad, useLocalState } from './hooks';
import AllTasks from './components/AllTasks';
import { ProgramCard, ProgramList, TaskView, TrackedList, TrackedProgram, WeeksView } from './types';

type View = 'tasks' | 'list' | 'calendar';
const STAGE_CHIP: Record<string, string> = { researching: '', applying: 'a', submitted: 'ok', decision: 'ok' };
const stageLabel = (s: string) => STAGES.find(([k]) => k === s)?.[1] ?? s;

/** My Programs: everything the student tracks, what is waiting for her, and what is coming up. */
export default function MyProgramsPage() {
  const history = useHistory();
  const { userId } = useEpUser();
  // All tasks first; the choice is remembered on this device.
  const [viewPref, setViewPref] = useLocalState<{ v: View }>('ep.myprograms.view', { v: 'tasks' });
  const view: View = (['tasks', 'list', 'calendar'] as View[]).includes(viewPref.v) ? viewPref.v : 'tasks';
  const setView = (v: View) => setViewPref({ v });
  const [calOpen, setCalOpen] = useState(false);

  const tracked = useLoad<TrackedList>(() => epApi.tracked(userId), [userId]);
  const weeks = useLoad<WeeksView>(() => epApi.weeks(userId), [userId]);
  const lists = useLoad<ProgramList[]>(() => epApi.lists(userId), [userId]);
  const saved = useLoad<ProgramCard[]>(() => epApi.saved(), []);

  const t = useMemo(() => tracked.data?.tracked ?? [], [tracked.data]);
  const limit = tracked.data?.plan_limit;
  const open = (pid: number) => history.push(`/summer-programs/programs?id=${pid}`);
  const trackedIds = new Set(t.map((x) => x.program_id));
  // New suggestions from a counselor or parent: a number on the Programs tab.
  const waiting = (lists.data ?? []).reduce((n, l) => n + (l.is_suggestion ? l.items.filter((i) => i.program && !i.in_student_programs && !i.student_response).length : l.created_by_role === 'parent' && l.items.length ? 1 : 0), 0);
  const savedOnly = (saved.data ?? []).filter((p) => !trackedIds.has(p.id));

  const comingUp = useMemo(() => {
    const today = todayIso();
    return t
      .flatMap((p) => p.items.filter((i) => i.date && i.date >= today && i.status !== 'done' && i.status !== 'not_applicable').map((i) => ({ ...i, program: p.name, program_id: p.program_id })))
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''))
      .slice(0, 6);
  }, [t]);

  const moveIt = async () => {
    const s = weeks.data?.suggestion;
    if (!s) return;
    try {
      await epApi.updateTask(userId, s.task_id, { student_target_date: s.to });
      toast.success(`Moved to ${md(s.to)}. Your calendar updates too.`);
      weeks.reload();
      tracked.reload();
    } catch (e) {
      showError(e);
    }
  };

  return (
    <div className="ep">
      <div className="mp">
        <div className="between" style={{ flexWrap: 'wrap' }}>
          <div>
            <h1>My programs</h1>
            <p className="body" style={{ marginTop: 4 }}>
              {limit?.free ? `Tracking ${limit.active} of ${limit.max} on the free plan` : `Tracking ${t.length} ${t.length === 1 ? 'program' : 'programs'}`}
            </p>
          </div>
          <div className="row">
            <div className="row" role="group" aria-label="View">
              {([['tasks', 'All tasks'], ['list', 'Programs'], ['calendar', 'Calendar']] as [View, string][]).map(([k, l]) => (
                <button key={k} type="button" className={`chipb ${view === k ? 'on' : ''}`} aria-pressed={view === k} onClick={() => setView(k)}>
                  {l}
                  {k === 'list' && waiting > 0 && <span className="ep-badge" aria-label={`${waiting} new suggestions`}>{waiting}</span>}
                </button>
              ))}
            </div>
            <button type="button" className="btn" onClick={() => history.push('/summer-programs')}>Find programs</button>
            <button type="button" className="btn" onClick={() => setCalOpen(true)}>Add to my calendar</button>
          </div>
        </div>

        {weeks.data && <ComingWeeks w={weeks.data} onMove={moveIt} />}

        {view === 'tasks' && (
          <AllTasks studentId={userId} viewer="student" tracked={t} onChanged={() => { tracked.reload(); weeks.reload(); }} />
        )}

        {view === 'list' && (
          <div className="mpgrid">
            <div>
              <WaitingForYou lists={lists.data ?? []} onOpen={open} onAnswered={() => { lists.reload(); saved.reload(); }} />
              <section className="card" aria-label="Tracking">
                <h2>Tracking</h2>
                {tracked.loading && !t.length ? (
                  <p className="muted" style={{ marginTop: 12 }}>Loading</p>
                ) : t.length === 0 ? (
                  <p className="body" style={{ marginTop: 12 }}>
                    Nothing yet. Open a program and add it to Tracking to get a checklist with dates.
                  </p>
                ) : (
                  t.map((p) => <TrackedRow key={p.program_id} p={p} onOpen={() => open(p.program_id)} />)
                )}
              </section>
              {savedOnly.length > 0 && (
                <section className="card" aria-label="Saved">
                  <h2>Saved</h2>
                  <p className="muted small" style={{ marginTop: 4 }}>Add one to Tracking to get its checklist and reminders.</p>
                  {savedOnly.map((p) => (
                    <div key={p.id} className="trow">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{programName(p)}</div>
                        <div className="muted small">{p.institution}</div>
                      </div>
                      <button type="button" className="btn sm" onClick={() => open(p.id)}>Add to Tracking</button>
                    </div>
                  ))}
                </section>
              )}
            </div>
            <aside className="card" aria-label="Coming up">
              <h2>Coming up</h2>
              {comingUp.length === 0 ? (
                <p className="body small" style={{ marginTop: 10 }}>No dates yet. Dates appear when you pick a plan, or when a program posts its deadline.</p>
              ) : (
                <div style={{ marginTop: 8 }}>
                  {comingUp.map((i) => (
                    <div key={i.id} className="up">
                      <span className="d">{md(i.date)}</span>
                      <div>
                        <div style={{ fontWeight: 500 }}>{i.title}</div>
                        <button type="button" className="linkish small" style={{ color: 'var(--ep-muted)', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => open(i.program_id)}>{i.program}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="muted small" style={{ marginTop: 12 }}>Reminders arrive 7 days and 1 day before each date.</p>
            </aside>
          </div>
        )}

        {view === 'calendar' && <MonthCalendar studentId={userId} tracked={t} onOpen={open} />}
      </div>
      {calOpen && <CalendarLinkModal studentId={userId} onClose={() => setCalOpen(false)} />}
    </div>
  );
}

function ComingWeeks({ w, onMove }: { w: WeeksView; onMove: () => void }) {
  const s = w.suggestion;
  return (
    <section className="card" style={{ marginTop: 16 }} aria-label="Coming weeks">
      <div className="between" style={{ flexWrap: 'wrap' }}>
        <h2>Coming weeks</h2>
        <span className="muted small">Programs, colleges and your own tasks together</span>
      </div>
      <div className="weeks">
        {w.weeks.map((k) => (
          <div key={k.week_start} className={`wk ${k.heavy ? 'heavy' : ''}`} title={k.items.map((i) => `${md(i.date)} ${i.title}`).join('\n')}>
            <span className="lbl" style={{ marginBottom: 2 }}>{md(k.week_start)}{k.heavy ? ' · heavy week' : ''}</span>
            <div style={{ fontWeight: 500 }}>{`${k.count} ${k.count === 1 ? 'item' : 'items'}`}</div>
          </div>
        ))}
      </div>
      {s && (
        <div className="between" style={{ marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <p style={{ color: 'var(--gm-warning-ink, #b45309)' }}>
            The week of {md(s.week_start)} has {w.weeks.find((k) => k.week_start === s.week_start)?.count ?? 'several'} things due. Move "{s.title}" to {md(s.to)}?
          </p>
          <button type="button" className="btn sm" onClick={onMove}>Move it</button>
        </div>
      )}
    </section>
  );
}

function WaitingForYou({ lists, onOpen, onAnswered }: { lists: ProgramList[]; onOpen: (id: number) => void; onAnswered: () => void }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const suggestions = lists.filter((l) => l.is_suggestion).flatMap((l) => l.items.filter((i) => i.program && !i.in_student_programs && !i.student_response).map((i) => ({ ...i, from: l.name, role: l.created_by_role })));
  const shared = lists.filter((l) => !l.is_suggestion && l.created_by_role === 'parent' && l.items.length);
  if (!suggestions.length && !shared.length) return null;

  const answer = async (itemId: number, response: 'added' | 'not_for_me', programId: number) => {
    try {
      await epApi.respond(itemId, response);
      onAnswered();
      if (response === 'added') onOpen(programId);
      else toast.success('Got it. Your counselor will see your answer.');
    } catch (e) {
      showError(e);
    }
  };

  return (
    <section className="card" aria-label="Suggested for you" style={{ marginBottom: 16 }}>
      <div className="between" style={{ alignItems: 'center' }}><h2>Suggested for you</h2><span className="muted small">From your counselor and your parents</span></div>
      {suggestions.map((i) => (
        <div key={i.id} className="trow">
          <div style={{ minWidth: 0 }}>
            <span className={`chip ${i.role === 'parent' ? 'ok' : 'w'}`}>{i.role === 'parent' ? 'From your parent' : 'From your counselor'}</span>
            <div style={{ fontWeight: 500, marginTop: 8 }}>{programName(i.program!)}</div>
            {i.note && <p className="body small" style={{ marginTop: 2 }}>{i.note}</p>}
          </div>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <button type="button" className="btn sm primary" onClick={() => answer(i.id, 'added', i.program!.id)}>Add to Tracking</button>
            <button type="button" className="btn sm" onClick={() => answer(i.id, 'not_for_me', i.program!.id)}>Not for me</button>
          </div>
        </div>
      ))}
      {shared.map((l) => (
        <div key={l.id} className="trow" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
          <div className="between" style={{ alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <span className="chip">A parent shared a list</span>
              <div style={{ fontWeight: 500, marginTop: 8 }}>{l.name}</div>
              <p className="body small" style={{ marginTop: 2 }}>{l.items.map((i) => (i.program ? programName(i.program) : '')).filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="btn sm" aria-expanded={expanded === l.id} onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
              {expanded === l.id ? 'Close' : 'Open'}
            </button>
          </div>
          {expanded === l.id && (
            <div style={{ marginTop: 8 }}>
              {l.items.filter((i) => i.program).map((i) => (
                <div key={i.id} className="between" style={{ padding: '8px 0', alignItems: 'center' }}>
                  <span>{programName(i.program!)}{i.in_student_programs ? <span className="muted small"> · in my programs</span> : null}</span>
                  <button type="button" className="btn sm" onClick={() => onOpen(i.program!.id)}>View</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

function TrackedRow({ p, onOpen }: { p: TrackedProgram; onOpen: () => void }) {
  const ns = p.next_step;
  return (
    <div className="trow">
      <div style={{ minWidth: 0 }}>
        <div className="row" style={{ gap: 8 }}>
          <span style={{ fontWeight: 500, fontSize: 15 }}>{p.name}</span>
          <span className={`chip ${STAGE_CHIP[p.stage]}`}>{p.decision ? DECISION_LABEL[p.decision] : stageLabel(p.stage)}</span>
          {p.program_status && p.program_status !== 'running' && <span className="chip a" title="Its tasks are paused. No reminders are sent.">{p.program_status === 'ended' ? 'Program ended' : 'Not running this year'}</span>}
        </div>
        {p.institution && <div className="muted small" style={{ fontWeight: 500, marginTop: 2 }}>{p.institution}</div>}
        {ns && (
          <div className="body small" style={{ marginTop: 6 }}>
            Next: {ns.title}
            {ns.date ? ` · ${ns.student_target_date ? 'your date' : 'deadline'} ${md(ns.date)}` : ''}
            {ns.created_by_role === 'counselor' && <span className="chip a" style={{ marginLeft: 6 }}>From your counselor</span>}
          </div>
        )}
        <div className="muted small" style={{ marginTop: 4 }}>
          {[deadlineText(p.deadline_kind, p.next_deadline, p.last_year_deadline_date), `${p.done_count} of ${p.total_count} done`, p.portal_url ? 'application link saved' : ''].filter(Boolean).join(' · ')}
        </div>
      </div>
      <button type="button" className="btn sm" onClick={onOpen}>Open</button>
    </div>
  );
}

/** Calendar: your target dates in blue; each program's deadline once, in red; your own tasks too. */
function MonthCalendar({ studentId, tracked, onOpen }: { studentId: number; tracked: TrackedProgram[]; onOpen: (pid: number) => void }) {
  const tasks = useLoad<TaskView[]>(() => epApi.tasks(studentId), [studentId, tracked]);
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const first = `${month}-01`;
  const startDow = new Date(`${first}T00:00:00Z`).getUTCDay();
  const gridStart = addDays(first, -startDow);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const names = new Map(tracked.map((p) => [p.program_id, p.name]));
  type Ev = { key: string; date: string; title: string; tip: string; kind: 'target' | 'deadline'; done: boolean; pid: number | null };
  const events: Ev[] = [];
  const seen = new Map<string, Ev & { all: TaskView[] }>();
  for (const t of (tasks.data ?? []).filter((x) => x.status !== 'not_applicable' && (x.program_id || x.origin === 'student_added' || x.origin === 'counselor_added'))) {
    const pname = t.program_id ? names.get(t.program_id) ?? 'A program' : 'My task';
    if (t.student_target_date) events.push({ key: `t${t.id}`, date: t.student_target_date, title: t.title, tip: `Your target: ${t.title} · ${pname}`, kind: 'target', done: t.status === 'done', pid: t.program_id });
    if (!t.official_deadline) continue;
    if (!t.program_id) { events.push({ key: `d${t.id}`, date: t.official_deadline, title: t.title, tip: `Deadline: ${t.title}`, kind: 'deadline', done: t.status === 'done', pid: null }); continue; }
    const k = `${t.program_id}|${t.official_deadline}`;
    const ev = seen.get(k);
    if (ev) { ev.all.push(t); ev.done = ev.all.every((x) => x.status === 'done'); continue; }
    const e = { key: `p${k}`, date: t.official_deadline, title: `Deadline: ${pname}`, tip: '', kind: 'deadline' as const, done: t.status === 'done', pid: t.program_id, all: [t] };
    seen.set(k, e);
    events.push(e);
  }
  for (const e of seen.values()) e.tip = `${e.title}: ${e.all.map((x) => x.title).join(', ')}`;
  events.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'deadline' ? -1 : 1));
  const shift = (n: number) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const title = new Date(`${first}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <section aria-label="Calendar" style={{ marginTop: 16 }}>
      <div className="between" style={{ alignItems: 'center' }}>
        <h2>{title}</h2>
        <div className="row">
          <button type="button" className="btn sm" onClick={() => shift(-1)} aria-label="Previous month">Previous</button>
          <button type="button" className="btn sm" onClick={() => setMonth(todayIso().slice(0, 7))}>Today</button>
          <button type="button" className="btn sm" onClick={() => shift(1)} aria-label="Next month">Next</button>
        </div>
      </div>
      <div className="cal">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d} className="h">{d}</div>)}
        {days.map((d) => (
          <div key={d} className={`c ${d.slice(0, 7) !== month ? 'out' : ''}`}>
            <span style={{ fontWeight: d === todayIso() ? 600 : 400 }}>{+d.slice(8)}</span>
            {events.filter((e) => e.date === d).map((e) => (
              <button key={e.key} type="button" className={`ev ${e.kind === 'deadline' ? 'dl' : ''} ${e.done ? 'done' : ''}`} style={{ border: 0, width: '100%', textAlign: 'left', font: 'inherit' }} title={e.tip} onClick={() => e.pid && onOpen(e.pid)}>
                {e.title}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="ep-legend">
        <span><i className="dot t" />Your target date</span>
        <span><i className="dot d" />Program deadline (once per program)</span>
      </div>
    </section>
  );
}

const FEED_PARTS: [string, string][] = [
  ['deadlines', 'Deadlines (shown once per program, not repeated for every task)'],
  ['targets', 'Target dates for each task'],
  ['mine', 'Tasks not tied to a program (own and counselor tasks)'],
  ['keydates', 'Test and financial aid dates (SAT, ACT, FAFSA)'],
];

/** Add to my calendar: one private link for Google, Apple or Outlook. One way: GradMap to the calendar. */
export function CalendarLinkModal({ studentId, onClose, studentName }: { studentId: number; onClose: () => void; studentName?: string }) {
  const link = useLoad<{ path: string }>(() => epApi.calendarLink(studentId), [studentId]);
  const [parts, setParts] = useLocalState<{ on: string[] }>('ep.calendar.parts', { on: FEED_PARTS.map(([k]) => k) });
  const on = parts.on.filter((k) => FEED_PARTS.some(([p]) => p === k));
  const base = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');
  const all = on.length === FEED_PARTS.length;
  const url = link.data ? `${base}${link.data.path.startsWith('/') ? '' : '/'}${link.data.path}${all ? '' : `?include=${on.join(',')}`}` : '';
  const webcal = url.replace(/^https?:/, 'webcal:');
  const google = `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`;
  const outlook = `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent('GradMap')}`;
  const copy = () => {
    navigator.clipboard?.writeText(url).then(() => toast.success('Link copied'), () => toast.error('Copy did not work. Select the link and copy it.'));
  };
  const toggle = (k: string) => setParts({ on: on.includes(k) ? on.filter((x) => x !== k) : [...on, k] });
  const ready = !!url && on.length > 0;
  return (
    <div className="ep-modal-bg" role="dialog" aria-modal="true" aria-labelledby="ep-cal-title" onClick={onClose}>
      <div className="ep-modal ep" style={{ padding: 24, minHeight: 0 }} onClick={(e) => e.stopPropagation()}>
        <h2 id="ep-cal-title">{studentName ? `Add ${studentName}'s dates to my calendar` : 'Add to my calendar'}</h2>
        <p className="body" style={{ marginTop: 8, lineHeight: 1.55 }}>
          {studentName
            ? `Puts ${studentName}'s program dates on the calendar you already use, on your phone and computer. It is one private link, and it keeps itself up to date.`
            : 'Puts your GradMap dates on the calendar you already use, on your phone and computer. It is one private link, and it keeps itself up to date.'}
        </p>
        <span className="lbl" style={{ marginTop: 14 }}>What goes on your calendar</span>
        {FEED_PARTS.map(([k, l]) => (
          <label key={k} className="ep-calpart">
            <input type="checkbox" checked={on.includes(k)} onChange={() => toggle(k)} /> {l}
          </label>
        ))}
        {link.error ? (
          <p className="panel" style={{ marginTop: 12 }}>The calendar link did not load. Please try again.</p>
        ) : (
          <div className="ep-calbtns">
            <a className={`btn ${ready ? '' : 'disabled'}`} href={google} aria-disabled={!ready} target="_blank" rel="noopener noreferrer">Google Calendar</a>
            <a className={`btn ${ready ? '' : 'disabled'}`} href={webcal || '#'} aria-disabled={!ready}>Apple Calendar</a>
            <a className={`btn ${ready ? '' : 'disabled'}`} href={outlook} aria-disabled={!ready} target="_blank" rel="noopener noreferrer">Outlook</a>
            <button type="button" className="btn" disabled={!ready} onClick={copy}>Copy link</button>
          </div>
        )}
        <ul className="body small" style={{ marginTop: 14, paddingLeft: 18, lineHeight: 1.6 }}>
          <li>Your calendar app asks one question: add this calendar? Tap Add. A GradMap calendar appears next to your own, in its own color.</li>
          <li>When a date changes in GradMap, your calendar updates too. Apple and Outlook update within a few hours. Google can take up to a day.</li>
          <li>It works one way. Changes you make in Google, Apple or Outlook do not come back to GradMap.</li>
          <li>Keep the link private, like a password.</li>
        </ul>
        <div className="foot">
          <span />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
